import { SourceLinks } from '@/components/ui/source-links';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { useState } from 'react';

interface GeographicSettingCardProps {
    geography: {
        primaryRegion: string;
        regionalContext: string;
        significantLocationRefs?: string[];
        sources?: Source[];
    };
    isExpanded?: boolean;
    onToggleExpansion?: () => void;
    defaultExpanded?: boolean;
    title?: string;
}

export function GeographicSettingCard({
    geography,
    isExpanded: controlledIsExpanded,
    onToggleExpansion: controlledOnToggleExpansion,
    defaultExpanded = false,
    title = "Geographic Setting"
}: GeographicSettingCardProps) {
    const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);

    const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
    const onToggleExpansion = controlledOnToggleExpansion !== undefined
        ? controlledOnToggleExpansion
        : () => setInternalIsExpanded(!internalIsExpanded);

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            title={title}
            icon={MapPin}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6 px-1">
                <div className="space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Primary Region</div>
                    <p className="text-sm text-foreground/70 font-light leading-relaxed">{geography.primaryRegion}</p>
                </div>
                <div className="space-y-1.5 border-l border-primary/10 pl-6">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Regional Context</div>
                    <p className="text-sm text-foreground/70 font-light leading-relaxed">{geography.regionalContext}</p>
                </div>
                {geography.significantLocationRefs && geography.significantLocationRefs.length > 0 && (
                    <div className="space-y-1.5 border-l border-primary/10 pl-6 md:col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Significant Locations</div>
                        <div className="flex flex-wrap gap-2 pt-1">
                            {geography.significantLocationRefs.map((loc, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px] uppercase tracking-widest bg-muted/20">
                                    {loc}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {geography.sources && geography.sources.length > 0 && (
                <div className="pt-4 border-t border-border/10">
                    <SourceLinks sources={geography.sources} />
                </div>
            )}
        </DiscoveryCard>
    );
}
